/* Where money that left an account went.
 *
 * The account read writes the free balance the exchange actually holds, so a
 * withdrawal arrives as a smaller number and the book's total falls with it.
 * The fall is real — the money is not there any more — but the value chart
 * draws it exactly like a losing week, and the one thing that would tell them
 * apart is a record only the person who made the transfer can give.
 *
 * Two answers, and they are different records rather than two labels on one:
 *
 * **Into another account.** The money is still in the book. It must not touch
 * the money-paid-in figure, because nothing was paid in — see
 * `state/convertCash.ts`, where the same rule is argued at length for currency
 * exchanges. Written as a conversion.
 *
 * **Out of the book.** A withdrawal, which has to reduce the capital paid in or
 * every return measured against it starts flattering itself. A cash flow with a
 * negative amount.
 *
 * ── two ways in, and the second one is the important one ─────────────────
 *
 * The first version opened only when the sync spotted the balance fall between
 * two reads. That catches everything from now on and **nothing that already
 * happened**: the comparison needs a previous reading, the first read after the
 * feature shipped is the one that stores it, and a withdrawal made before then
 * is invisible for ever. Which is exactly the case the feature was built for.
 *
 * So it also opens by hand, from the leverage screen and from the cash card,
 * with the source account picked rather than assumed. The detected case
 * prefills the amount and offers "it was not a transfer"; the manual case asks
 * for everything and offers cancel. Same records either way — a transfer
 * recorded a week late is the same transfer.
 *
 * When it is opened by hand the source balance has usually **not** been settled
 * yet, so the debit is real; when it comes from a detected fall the exchange
 * has already taken it off and the sync has read that back, so only the far
 * side moves. That is what the checkbox under the amount is for, and it is
 * prefilled from which way the panel was opened rather than left as a puzzle.
 *
 * The fee is the gap between the two amounts rather than a field of its own.
 * Exchanges quote a withdrawal fee and then take something slightly different,
 * and what matters is that the book agrees with the two balances that actually
 * exist. Typing what left and what arrived cannot disagree with them. */

import { useState } from 'react';
import { useStore } from '../state/store';
import { useDisplay } from '../state/display';
import { accountName } from '../lib/accounts';
import { money, moneyExact } from '../lib/format';
import { decimalOr } from '../lib/number';
import { newId } from '../lib/id';
import type { CashConversion } from '../types';

const OUT_OF_BOOK = '__out';

/** The detected panel: shown only when the sync noticed a fall it cannot
    explain. Opens with the amount filled in and the source already settled. */
export function AccountOutflow({ say }: { say: (m: string) => void }) {
  const { state } = useStore();
  const pending = state.settings.mexcUnaccounted ?? 0;
  const from = state.data.accounts.find((a) => a.isFutures) ?? null;
  if (!(pending > 0) || !from) return null;
  return <MoveMoney say={say} detected={pending} fromAccount={from.id} />;
}

/** The same thing opened by hand, for a transfer the app never saw. */
export function MoveMoneyButton({ say, from }: { say: (m: string) => void; from?: string }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        Record a transfer or withdrawal
      </button>
    );
  }
  return (
    <MoveMoney
      say={(m) => { say(m); setOpen(false); }}
      {...(from ? { fromAccount: from } : {})}
      onCancel={() => setOpen(false)}
    />
  );
}

function MoveMoney({
  say, detected, fromAccount, onCancel,
}: {
  say: (m: string) => void;
  /** The unexplained fall, when the sync found one. */
  detected?: number;
  fromAccount?: string;
  onCancel?: () => void;
}) {
  const { state, convertCash, putCashFlow, saveSettings, setCash } = useStore();
  const { currency, toDisplay } = useDisplay();
  const accounts = state.data.accounts;

  const [from, setFrom] = useState(fromAccount ?? accounts[0]?.id ?? '');
  const [left, setLeft] = useState(detected ? String(Number(detected.toFixed(2))) : '');
  const [arrived, setArrived] = useState('');
  const [to, setTo] = useState('');
  /* Whether the money has already come off the source. True for a fall the sync
     read back off the exchange, false for a transfer being written down before
     anybody has re-read the balance. Getting this wrong is a double entry in
     one direction and a missing one in the other, so it is on screen. */
  const [settled, setSettled] = useState(detected !== undefined);
  const [busy, setBusy] = useState(false);

  const fromCurrency = state.data.cash.find((c) => c.account === from)?.currency ?? 'USD';
  const leftAmount = decimalOr(left);
  const arrivedAmount = decimalOr(arrived);
  const intoBook = to !== '' && to !== OUT_OF_BOOK;
  const fee = intoBook && arrivedAmount > 0 ? leftAmount - arrivedAmount : 0;
  const ready = from !== '' && leftAmount > 0 && to !== '' && (!intoBook || arrivedAmount > 0);

  const clearPending = () =>
    (detected !== undefined ? saveSettings({ mexcUnaccounted: 0, mexcUnaccountedAt: '' })
      : Promise.resolve());

  const save = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const at = new Date().toISOString();
      if (intoBook) {
        const toCurrency = state.data.cash.find((c) => c.account === to)?.currency ?? fromCurrency;
        const record: CashConversion = {
          id: newId('conv'), at,
          fromAccount: from, fromAmount: leftAmount, fromCurrency,
          toAccount: to, toAmount: arrivedAmount, toCurrency,
          note: `Off ${accountName(accounts, from)}`,
        };
        await convertCash(record, { sourceAlreadySettled: settled });
        say(`Recorded. ${moneyExact(arrivedAmount, toCurrency as 'USD')} into `
          + `${accountName(accounts, to)}${fee > 0 ? `, ${moneyExact(fee, fromCurrency as 'USD')} in fees` : ''}.`);
      } else {
        /* Out of the book: a withdrawal, which comes off the money paid in. The
           balance moves only when it has not already. */
        await putCashFlow({
          id: newId('flow'), at, account: from,
          amount: -leftAmount, currency: fromCurrency,
        });
        if (!settled) {
          /* The money has not come off yet, so it comes off here. Where the
             balance was already read back after the transfer this is skipped,
             which is the same guard the conversion path takes. */
          const held = state.data.cash.find((c) => c.account === from);
          await setCash(from, (held?.amount ?? 0) - leftAmount, held?.currency ?? fromCurrency);
        }
        say(`Recorded as a withdrawal of ${moneyExact(leftAmount, fromCurrency as 'USD')}.`);
      }
      await clearPending();
      onCancel?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card-pad" style={{
      marginTop: 10,
      ...(detected !== undefined ? { borderColor: 'var(--warn-edge)' } : {}),
    }}>
      {detected !== undefined ? (
        <>
          <div className="alert-kind" style={{ color: 'var(--warn)' }}>
            <span aria-hidden="true">▲</span>WHERE DID THIS GO
          </div>
          <div className="alert-headline">
            {money(toDisplay(detected), currency)} went out of {accountName(accounts, from)}
          </div>
          <div className="alert-body">
            The balance fell by that much between two reads, and posting margin does not account
            for it. Money that went to another account is still in the book and is not a deposit;
            money that went out of the book is a withdrawal and comes off what you have paid in.
            If it was a contract the exchange settled, nothing needs recording.
          </div>
        </>
      ) : (
        <>
          <div className="fin-title">Record a transfer or withdrawal</div>
          <div className="alert-body">
            Money moved between your own accounts is not a deposit and must not count as one. Money
            that went out of the book is a withdrawal and comes off what you have paid in. Either way the
            fee is whatever did not arrive.
          </div>
        </>
      )}

      {detected === undefined ? (
        <div className="field" style={{ marginTop: 14 }}>
          <label htmlFor="mv-from">Where it came from</label>
          <select id="mv-from" className="select" value={from}
                  onChange={(e) => setFrom(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      ) : null}

      <div className="field" style={{ ...(detected !== undefined ? { marginTop: 14 } : {}) }}>
        <label htmlFor="mv-left">How much went out ({fromCurrency})</label>
        <input id="mv-left" className="input" data-mono="true" inputMode="decimal"
               value={left} placeholder="0.00"
               onChange={(e) => setLeft(e.target.value)} />
        {detected !== undefined ? (
          <div className="hint">The figure above is what the balance fell by. Change it if the
            transfer was a different amount.</div>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="mv-to">Where it went</label>
        <select id="mv-to" className="select" value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="">Choose…</option>
          {accounts.filter((a) => a.id !== from).map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
          <option value={OUT_OF_BOOK}>Out of the book — a withdrawal</option>
        </select>
      </div>

      {intoBook ? (
        <div className="field">
          <label htmlFor="mv-arrived">How much arrived</label>
          <input id="mv-arrived" className="input" data-mono="true" inputMode="decimal"
                 value={arrived} placeholder="0.00"
                 onChange={(e) => setArrived(e.target.value)} />
          <div className="hint">
            {arrivedAmount > 0
              ? fee > 0
                ? <>Fees took <span className="num">{moneyExact(fee, fromCurrency as 'USD')}</span> of it.</>
                : fee < 0
                  ? <>More arrived than went out, so this reads as no fee. Check the two figures.</>
                  : <>Nothing was lost to fees.</>
              : <>What actually landed, after whatever was taken. The difference is the fee, so the
                 book agrees with both balances rather than with a quoted rate.</>}
          </div>
        </div>
      ) : null}

      {/* The one thing that cannot be worked out from the outside. */}
      <div className="field">
        <label htmlFor="mv-settled" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input id="mv-settled" type="checkbox" checked={settled}
                 onChange={(e) => setSettled(e.target.checked)} />
          <span>The balance already shows this money gone</span>
        </label>
        <div className="hint">
          {settled
            ? <>Nothing comes off {accountName(accounts, from)} here. Leave this on when the
               balance was read back from the exchange after the transfer, or when you have
               already corrected it by hand.</>
            : <>{moneyExact(leftAmount, fromCurrency as 'USD')} comes off{' '}
               {accountName(accounts, from)} when this is saved.</>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <button className="btn btn-primary" disabled={!ready || busy} onClick={() => void save()}>
          Record it
        </button>
        {detected !== undefined ? (
          <button className="btn btn-ghost" disabled={busy} onClick={() => void clearPending()}>
            It was not a transfer
          </button>
        ) : (
          <button className="btn btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
        )}
      </div>
    </div>
  );
}
